import { useEffect, useState } from "react";

// Define the possible screen sizes as a const array for better type inference
const SCREEN_SIZES = ["xs", "sm", "md", "lg", "xl", "2xl"] as const;

// Create a union type from the array
export type ScreenSize = (typeof SCREEN_SIZES)[number];

// Type-safe size order mapping
const sizeOrder: Record<ScreenSize, number> = {
    xs: 0,
    sm: 1,
    md: 2,
    lg: 3,
    xl: 4,
    "2xl": 5,
} as const;

class ComparableScreenSize {
    constructor(private value: ScreenSize) {}

    toString(): ScreenSize {
        return this.value;
    }

    valueOf(): number {
        return sizeOrder[this.value];
    }

    // Add type predicate methods for better TypeScript support
    equals(other: ScreenSize): boolean {
        return this.value === other;
    }

    lessThan(other: ScreenSize): boolean {
        return this.valueOf() < sizeOrder[other];
    }

    greaterThan(other: ScreenSize): boolean {
        return this.valueOf() > sizeOrder[other];
    }

    lessThanOrEqual(other: ScreenSize): boolean {
        return this.valueOf() <= sizeOrder[other];
    }

    greaterThanOrEqual(other: ScreenSize): boolean {
        return this.valueOf() >= sizeOrder[other];
    }
}

// Helper function to get screen size based on width
const getScreenSizeFromWidth = (width: number): ScreenSize => {
    if (width >= 1536) return "2xl";
    if (width >= 1280) return "xl";
    if (width >= 1024) return "lg";
    if (width >= 768) return "md";
    if (width >= 640) return "sm";
    return "xs";
};

// Get initial screen size, safely handling SSR
const getInitialScreenSize = (): ScreenSize => {
    if (typeof window === "undefined") return "xs";
    return getScreenSizeFromWidth(window.innerWidth);
};

const useScreenSize = (): ComparableScreenSize => {
    // AIDEV-NOTE: Start with "xs" for both server and client to avoid hydration mismatch
    const [screenSize, setScreenSize] = useState<ScreenSize>("xs");

    useEffect(() => {
        const handleResize = () => {
            setScreenSize(getScreenSizeFromWidth(window.innerWidth));
        };

        // Only add listener if we're in browser context
        if (typeof window !== "undefined") {
            // AIDEV-NOTE: Set the correct initial size after hydration
            setScreenSize(getScreenSizeFromWidth(window.innerWidth));

            window.addEventListener("resize", handleResize);
            return () => window.removeEventListener("resize", handleResize);
        }
    }, []);

    return new ComparableScreenSize(screenSize);
};

export { useScreenSize };
